import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { StyleSheet, css } from 'aphrodite'
import { useTranslation } from 'react-i18next'
import { COLOR_BLACK, COLOR_WHITE } from '../../lib/colors'
import UIButtonIcon from '../../components/UI/UIButtonIcon'
import { IconClose, IconChevronUp } from '../../components/Icon'
import minimizeIcon from '../../assets/minimize-screen-icon.svg'
import UIProgressCircle from '../../components/UI/UIProgressCircle'
import UILogo from '../../components/UI/UILogo'
import UIButton from '../../components/UI/UIButton'
import LiveContext from '../../components/Context/LiveContext'
import Violence from '../../assets/Violence.svg'
import Sex from '../../assets/Sexe.svg'
import Eye from '../../assets/Noeye.svg'
import { MEDIA_MIN_SM, MEDIA_MAX_SM, MEDIA_MAX_XS } from '../../lib/breakpoints'

function LivePending() {
  const { t } = useTranslation('translation')
  const [progressBarPoint, setProgressBarPoint] = useState(0)

  const {
    toggleLivePreview,
    isLivePreviewMinimized,
    toggleLivePreviewMinimized,
  } = useContext(LiveContext)

  const handleClose = (e) => {
    e.preventDefault()
    toggleLivePreview(false)
  }

  const handleExpand = (e) => {
    e.preventDefault()
    toggleLivePreviewMinimized(false)
  }

  const handleMinimize = () => {
    toggleLivePreviewMinimized(true)
  }

  useEffect(() => {
    if (progressBarPoint >= 100) {
      setProgressBarPoint(0)
    } else {
      setTimeout(() => {
        setProgressBarPoint(progressBarPoint + 5)
      }, 100)
    }
  }, [progressBarPoint])

  const listInstructions = [
    {
      title: t('pages.livePreview.titleInstruction1'),
      description: t('pages.livePreview.descriptionInstruction1'),
      image: Violence,
    },
    {
      title: t('pages.livePreview.titleInstruction2'),
      description: t('pages.livePreview.descriptionInstruction2'),
      image: Sex,
    },
    {
      title: t('pages.livePreview.titleInstruction3'),
      description: t('pages.livePreview.descriptionInstruction3'),
      image: Eye,
    },
  ]

  const renderInstructions = () => (
    <div className={css(styles.containerInstructions)}>
      {listInstructions.map(({ title, description, image }, index) =>
        window.innerWidth > 480 ? (
          <Instruction
            key={index}
            title={title}
            img={image}
            description={description}
          />
        ) : (
          <InstructionMobile
            key={index}
            title={title}
            img={image}
            description={description}
          />
        )
      )}
    </div>
  )

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.headerBeginLive)}>
        <UIButtonIcon
          dynamicStyle={styles.iconClose}
          onClick={isLivePreviewMinimized ? handleExpand : handleClose}
        >
          {isLivePreviewMinimized ? (
            <IconChevronUp dynamicStyle={styles.iconExpand} />
          ) : (
            <IconClose />
          )}
        </UIButtonIcon>
        <h2
          className={css(
            styles.title,
            isLivePreviewMinimized && styles.titleMinimize
          )}
        >
          {t('pages.livePreview.title')}
        </h2>
        <div className={css(styles.containerProgress)}>
          <UIProgressCircle
            percent={progressBarPoint}
            showPercent={false}
            width={window.innerWidth > 768 ? '150' : '75'}
          >
            <UILogo />
          </UIProgressCircle>
        </div>
      </div>
      {!isLivePreviewMinimized && (
        <>
          {renderInstructions()}
          <div className={css(styles.minimizeButton)} onClick={handleMinimize}>
            <img
              src={minimizeIcon}
              className={css(styles.minimizeIcon)}
              alt="minimiser-icon"
            />
            <UIButton
              label={t('pages.livePreview.minimizePage')}
              dynamicStyle={styles.button}
              textStyle={styles.text}
            />
          </div>
        </>
      )}
      {isLivePreviewMinimized && (
        <>
          <h3 className={css(styles.titleInstructionMinimized)}>
            {t('pages.livePreview.titleInstructionMinimized')}
          </h3>
          <p className={css(styles.instructionMinimized)}>
            {t('pages.livePreview.instructionMinimized1')}
          </p>
          <p className={css(styles.instructionMinimized)}>
            {t('pages.livePreview.instructionMinimized2')}
          </p>
          <p className={css(styles.instructionMinimized)}>
            {t('pages.livePreview.instructionMinimized3')}
          </p>
          <p className={css(styles.instructionMinimized)}>
            {t('pages.livePreview.instructionMinimized4')}
          </p>
          <div className={css(styles.liveInProgress)}>
            {t('pages.livePreview.liveInProgress')}
          </div>
        </>
      )}
    </div>
  )
}

const Instruction = ({ title, description, img }) => {
  return (
    <div className={css(styles.containerInstruction)}>
      <img className={css(styles.illustration)} src={img} />
      <p className={css(styles.titleInstruction)}>{title.toUpperCase()}</p>
      <p className={css(styles.descriptionInstruction)}>{description}</p>
    </div>
  )
}

const InstructionMobile = ({ title, description, img }) => {
  return (
    <div className={css(styles.containerInstructionMobile)}>
      <img className={css(styles.illustrationMobile)} src={img} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <p className={css(styles.titleInstructionMobile)}>
          {title.toUpperCase()}
        </p>
        <p className={css(styles.descriptionInstructionMobile)}>
          {description}
        </p>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: '999',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: COLOR_WHITE,
    overflow: 'hidden',
    color: COLOR_BLACK,
    fontWeight: 700,
    letterSpacing: '-0.05em',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 1s ease',
  },
  containerMinimize: {
    transformOrigin: 'right bottom',
    transform: 'scale(0.3)',
    borderRadius: '20px',
    right: '20px',
    bottom: '20px',
    transition: 'all 1s ease',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    [MEDIA_MIN_SM]: {
      fontSize: '27.6pt',
    },

    [MEDIA_MAX_SM]: {
      fontSize: '24pt',
    },

    [MEDIA_MAX_XS]: {
      fontSize: '18pt',
    },
  },
  titleMinimize: {
    fontSize: '3.5em',
    marginBottom: '0.5em',
  },
  containerInstructionMobile: {
    display: 'flex',
    flexDirection: 'row',
    margin: '0.5em',
  },
  illustrationMobile: {
    width: '4.5em',
    height: '4.5em',
    alignSelf: 'center',
    margin: '0.5em',
  },
  titleInstructionMobile: {
    marginLeft: '10px',
  },
  descriptionInstructionMobile: {
    fontWeight: 300,
    margin: 0,
  },
  containerInstruction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '250px',
    margin: '0px 20px',

    [MEDIA_MAX_SM]: {
      fontSize: '16px',
    },
  },
  illustration: {
    width: '6.5em',
    height: '6.5em',
    marginBottom: '10px',
    borderRadius: '10px',
  },
  titleInstruction: {
    margin: 0,
    fontWeight: 600,
    textAlign: 'center',
  },
  descriptionInstruction: {
    textAlign: 'center',
    fontWeight: 300,
  },
  containerInstructions: {
    display: 'flex',
    width: '100%',

    [MEDIA_MIN_SM]: {
      justifyContent: 'center',
      padding: '0em 15em',
      marginTop: '2em',
    },

    [MEDIA_MAX_SM]: {
      justifyContent: 'space-evenly',
      padding: '3.5vh 1em',
      flexWrap: 'wrap',
    },

    [MEDIA_MAX_XS]: {
      fontSize: '14px',
    },
  },
  logo: {
    width: '1.6em',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  containerProgress: {
    margin: '1em 0em',
    position: 'relative',
  },
  UBIlogo: {
    width: 'unset',
    height: 'unset',
  },
  minimizeButton: {
    borderRadius: '50px',
    border: '3px solid black',
    backgroundColor: COLOR_WHITE,
    color: COLOR_BLACK,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    width: 'fit-content',
    alignSelf: 'flex-end',
    marginRight: '20px',
    position: 'fixed',
    bottom: 'calc(var(--vh, 1vh) * 5)',
  },
  button: {
    backgroundColor: COLOR_WHITE,
    color: COLOR_BLACK,
    fontWeight: 700,
    padding: 0,
  },
  text: {
    fontWeight: 700,
    letterSpacing: '-0.03em',
    lineHeight: '14px',
  },
  headerBeginLive: {
    [MEDIA_MAX_SM]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1.2em',
      height: '50px',
      whiteSpace: 'nowrap',
      margin: '0',
    },
  },
  iconClose: {
    cursor: 'pointer',
    [MEDIA_MIN_SM]: {
      padding: '15px',
      position: 'absolute',
      top: '20px',
      left: '20px',
    },
  },
  iconExpand: {
    width: '75px',
    height: '75px',
  },
  liveInProgress: {
    position: 'absolute',
    bottom: 0,
    backgroundColor: COLOR_BLACK,
    color: COLOR_WHITE,
    height: '100px',
    width: '100%',
    opacity: '.3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: 0,
    fontSize: '0em',
  },
  titleInstructionMinimized: {
    fontSize: '2.2em',
    margin: '10px 0px',
  },
  instructionMinimized: {
    fontSize: '1.8em',
    fontWeight: 400,
    margin: '10px 0px',
  },
})

Instruction.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  img: PropTypes.string,
}

InstructionMobile.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  img: PropTypes.string,
}

LivePending.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
}

export default LivePending
